import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaUserFriends, FaClock, FaCalendarAlt, FaUser, FaPhone } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/ReservationForm.css';
import sushiImage from '../assets/images/susii.webp';
import { GiNoodles, GiRiceCooker, GiChopsticks } from 'react-icons/gi';
import ReCAPTCHA from 'react-google-recaptcha';
import { Circles } from 'react-loader-spinner'; // 🔄 Spinner added
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../utils/apiConfig';



function ReservationForm() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        persons: '1',
        date: null,
        time: '',
    });

    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false); // 🟡 loading state
    const [isSuccess, setIsSuccess] = useState(false); // ✅ error/success flag
    const recaptchaRef = useRef();
    const API_BASE = getApiBaseUrl();


    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleDateChange = (date) => {
        setFormData({ ...formData, date });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.date) {
            setMessage(t('reservation.dateRequired'));
            setIsSuccess(false);
            return;
        }

        setLoading(true);
        setMessage('');
        setIsSuccess(false);

        try {
            // const captchaToken = await recaptchaRef.current.executeAsync();
            // recaptchaRef.current.reset();

            const response = await fetch(`${API_BASE}/api/reserve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    phone: formData.phone,
                    persons: formData.persons,
                    date: formData.date.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' }),
                    time: formData.time,
                }),
            });

            const result = await response.json();

            if (response.ok) {
                setMessage(t('reservation.successMessage'));
                setIsSuccess(true);
                setFormData({ name: '', phone: '', persons: '1', date: null, time: '' });
            } else {
                setMessage(`❌ ${result.error || t('reservation.reservationFailed')}`);
                setIsSuccess(false);
            }
        } catch (error) {
            setMessage(t('reservation.networkError'));
            setIsSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="reservation-background"
            style={{ backgroundImage: `url(${sushiImage})` }}
        >
            <div className="reservation-overlay" />
            <motion.div
                className="reservation-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="reservation-left">
                    <button className="btn-subtitle">{t('reservation.subtitle')}</button>
                    <h2 className="reservation-title">
                        {t('reservation.title')}
                        <GiNoodles style={{ marginRight: '10px' }} />
                        <GiRiceCooker style={{ marginRight: '10px' }} />
                        <GiChopsticks />
                    </h2>

                    <form onSubmit={handleSubmit} className="reservation-form">
                        <label>
                            <FaUser className="icon" />
                            {t('reservation.name')}
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Ihr Name"
                                required
                            />
                        </label>

                        <label>
                            <FaPhone className="icon" />
                            {t('reservation.phone')}
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="z.B. 0151 12345678"
                                pattern=".{6,}"
                                required
                            />
                        </label>

                        <label>
                            <FaUserFriends className="icon" />
                            {t('reservation.persons')}
                            <select
                                name="persons"
                                value={formData.persons}
                                onChange={handleChange}
                                required
                            >
                                {[...Array(9)].map((_, i) => {
                                    const count = i + 1;
                                    return (
                                        <option key={count} value={count}>
                                            {count} {count === 1 ? t('personsSuffix.one') : t('personsSuffix.other')}
                                        </option>
                                    );
                                })}
                                <option value="10+">10+ {t('personsSuffix.other')}</option>
                            </select>
                        </label>

                        <label>
                            <FaCalendarAlt className="icon" />
                            {t('reservation.date')}
                            <DatePicker
                                selected={formData.date}
                                onChange={handleDateChange}
                                dateFormat="dd.MM.yyyy"
                                minDate={new Date()}
                                placeholderText="TT.MM.JJJJ"
                                required
                            />
                        </label>

                        <label>
                            <FaClock className="icon" />
                            {t('reservation.time')}
                            <select
                                name="time"
                                value={formData.time}
                                onChange={handleChange}
                                required
                            >
                                <option value="" disabled>
                                    {t('reservation.selectTime')}
                                </option>
                                {t('timeOptions', { returnObjects: true }).map((time) => (

                                    <option key={time} value={time}>
                                        {time} Uhr
                                    </option>
                                )
                                )}
                            </select>
                        </label>

                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            size="normal"
                            ref={recaptchaRef}
                        />

                        <button type="submit" className="btn-submitt" disabled={loading}>
                            {loading ? (
                                <Circles height="24" width="24" color="#fff" ariaLabel="loading" />
                            ) : (
                                t('reservation.reserve')
                            )}
                        </button>
                    </form>

                    {message && (
                        <p
                            className={`message-box ${isSuccess ? 'success' : 'error'}`}
                            dangerouslySetInnerHTML={{ __html: message }}
                        />
                    )}
                </div>
            </motion.div>
        </section>
    );
}

export default ReservationForm;
